import React from "react";
import { FormattedNumber } from "react-intl";
import { gql, useMutation, useQuery } from "@apollo/client";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Fab,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  Container,
  Stack,
} from "@mui/material";
import {
  Plus as CreateIcon,
  DotsVertical as MenuIcon,
  Cancel as DisabledIcon,
} from "mdi-material-ui";

import Layout from "components/common/Layout";
import CreateReferralRewardDialog from "components/referralReward/CreateReferralRewardDialog";

const MUTATION = gql`
  mutation ($referralRewardId: ID!, $disabled: Boolean!) {
    UpdateReferralReward(id: $referralRewardId, disabled: $disabled) {
      referralReward {
        id
        disabled
      }
    }
  }
`;

function ReferralRewardTableRow({ referralReward, onUpdate }) {
  const menuRef = React.useRef();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const [updateReferralReward, { loading }] = useMutation(MUTATION);

  return (
    <TableRow
      sx={{
        "&:nth-of-type(odd)": { bgcolor: (t) => t.palette.background.odd },
        "&:nth-of-type(even)": { bgcolor: (t) => t.palette.background.even },
      }}
    >
      <TableCell>
        <Typography>{referralReward.label["fr"]}</Typography>
      </TableCell>

      <TableCell align="right">
        <FormattedNumber value={referralReward.points} />
      </TableCell>

      <TableCell align="center" padding="checkbox">
        {referralReward.disabled && <DisabledIcon />}
      </TableCell>

      <TableCell padding="checkbox">
        <IconButton
          size="small"
          ref={menuRef}
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <Menu
          anchorEl={menuRef.current}
          keepMounted
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        >
          <MenuItem
            disabled={loading}
            onClick={async () => {
              updateReferralReward({
                variables: {
                  referralRewardId: referralReward.id,
                  disabled: !referralReward.disabled,
                },
              }).then(() => {
                setMenuOpen(false);
                return onUpdate?.();
              });
            }}
          >
            {referralReward.disabled ? "Ré-activer" : "Désactiver"}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

const QUERY = gql`
  query ($disabled: Boolean) {
    referralRewards(disabled: $disabled) {
      edges {
        cursor
        node {
          id
          label {
            en
            fr
          }
          points
          disabled
        }
      }
    }
  }
`;

export default function ReferralRewardsPage() {
  const [showDisabled, setShowDisabled] = React.useState(false);

  const { data, loading, refetch } = useQuery(QUERY, {
    variables: { disabled: showDisabled ? undefined : false },
    fetchPolicy: "network-only",
  });

  return (
    <Container fixed>
      <Layout.Toolbar fixed>
        <Stack spacing={2} direction="row" alignItems="center">
          <CreateReferralRewardDialog onCreate={refetch}>
            {({ open }) => (
              <Fab onClick={open} size="small" color="primary">
                <CreateIcon />
              </Fab>
            )}
          </CreateReferralRewardDialog>

          <Typography variant="h4" color="primary" sx={{ flexGrow: 1 }}>
            Récompenses de parrainage
          </Typography>

          <div>
            <FormControl>
              <FormControlLabel
                label="Afficher les récompenses désactivés"
                control={
                  <Switch
                    checked={showDisabled}
                    onChange={(evt, checked) => setShowDisabled(checked)}
                  />
                }
              />
            </FormControl>
          </div>
        </Stack>
      </Layout.Toolbar>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell align="right">Points</TableCell>
              <TableCell padding="checkbox" />
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan="5" align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {data?.referralRewards.edges.map(({ cursor, node }) => (
              <ReferralRewardTableRow key={cursor} referralReward={node} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
